import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { mdiChevronDown, mdiGauge, mdiPlus } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { ZoneBEquipmentByTrade } from './ZoneBEquipmentByTrade'
import { AddEquipmentDialog } from './AddEquipmentDialog'
import { AddMetersDialog } from './AddMetersDialog'
import type { FilterState } from './EquipmentFilterBar'
import {
  useEquipmentTab,
  type EquipmentTabFilters,
} from '../../hooks/useEquipmentTab'
import {
  EQUIPMENT_CONDITIONS,
  type EquipmentCondition,
} from '../../api/equipment-tab-types'
import { useAssetTypes, useChildAssetTypes } from '../../hooks/useQueries'
import { Button } from '../ui/button'
import { EquipmentViaProvider } from './EquipmentViaContext'
import { useAssetEquipment } from '../../hooks/useAssetEquipment'
import { isLotTypeCode } from '../../lib/asset-type-tree'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu'
import { METERING_TRADE_CODE } from './trades'
import { MeterSupplyProvider } from '../meters/MeterSupplyContext'
import { ServesContext, useSubtreeServes } from '../asset-relations/NetworksSection'

interface EquipmentTabProps {
  org: string
  assetUid: string
  /**
   * Code of the current asset's type (e.g. `unit`, `building`). Used to scope
   * the trade list to trades that are actually valid under this asset type —
   * resolved from the type code so it doesn't depend on the paginated asset
   * list (which would otherwise fail open and show every trade on a deep lot).
   */
  assetTypeCode?: string
}

/**
 * Top-level component for the Equipment tab. Composes zone A and zone B,
 * keeps filter state in the URL (`?q`, `?conditions`, `?trades`), and
 * tolerates a backend that hasn't shipped the endpoint yet by rendering the
 * scaffolding with an empty state.
 */
export function EquipmentTab({ org, assetUid, assetTypeCode }: EquipmentTabProps) {
  const { t } = useTranslation('assets')

  // Add-Equipment dialog state — owned here so per-trade `+` buttons and the
  // top-level "Add equipment" button share a single instance.
  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogTradeUid, setDialogTradeUid] = useState<string | undefined>(undefined)
  const [metersOpen, setMetersOpen] = useState(false)
  // "Add a component" from a row (spec 2026-10-27-17): the assembly's uid.
  const [componentOf, setComponentOf] = useState<string | undefined>(undefined)
  // Include possible-parent relations so we can scope trades by the current
  // asset type without depending on the paginated asset list.
  const { data: assetTypes = [] } = useAssetTypes(org, true)

  // Fallback resolution via the child-type lookup (subject to the paginated
  // asset list) for callers that don't pass an assetTypeCode.
  const { data: allowedChildTypes = [], parentAssetTypeUid } = useChildAssetTypes(
    org,
    assetUid,
  )

  // Trades that are valid under this asset type. Preferred path: resolve the
  // current asset type by its code and keep only trades whose
  // possible_parent_asset_type_uids include it — this is why building-envelope
  // trades (Structure, Toiture, Façade, Aménagement extérieur) no longer show
  // on a lot. Falls back to the child-type lookup, and finally fails open
  // (undefined = no restriction) while data is still loading.
  const allowedTradeCodes = useMemo<string[] | undefined>(() => {
    const currentType = assetTypeCode
      ? assetTypes.find((at) => at.code === assetTypeCode)
      : undefined
    if (currentType) {
      return assetTypes
        .filter((at) => {
          const kind = (at as { kind?: string }).kind
          return kind === 'trade' || (typeof at.code === 'string' && at.code.startsWith('trade_'))
        })
        .filter((at) =>
          ((at as { possible_parent_asset_type_uids?: string[] }).possible_parent_asset_type_uids || [])
            .includes(currentType.uid),
        )
        .map((at) => at.code)
    }
    if (!parentAssetTypeUid) return undefined
    return (allowedChildTypes as Array<{ code: string }>).map((at) => at.code)
  }, [assetTypes, assetTypeCode, parentAssetTypeUid, allowedChildTypes])

  // Read filter state from URL on mount + every popstate.
  const readFiltersFromUrl = useCallback((): FilterState => {
    if (typeof window === 'undefined') {
      return { q: '', conditions: [], trades: [] }
    }
    const sp = new URLSearchParams(window.location.search)
    const q = sp.get('q') || ''
    const conditionsRaw = sp.get('conditions') || ''
    const tradesRaw = sp.get('trades') || ''
    const conditions = conditionsRaw
      .split(',')
      .map((c) => c.trim())
      .filter((c): c is EquipmentCondition =>
        EQUIPMENT_CONDITIONS.includes(c as EquipmentCondition),
      )
    const trades = tradesRaw
      .split(',')
      .map((c) => c.trim())
      .filter(Boolean)
    return { q, conditions, trades }
  }, [])

  const [filters, setFilters] = useState<FilterState>(readFiltersFromUrl)

  // Mirror filters to URL whenever they change. Use replaceState so the back
  // button doesn't get polluted with intermediate filter changes.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const url = new URL(window.location.href)
    const set = (key: string, val: string) => {
      if (val) url.searchParams.set(key, val)
      else url.searchParams.delete(key)
    }
    set('q', filters.q)
    set('conditions', filters.conditions.join(','))
    set('trades', filters.trades.join(','))
    window.history.replaceState({}, '', url.toString())
  }, [filters])

  // Keep state in sync with browser back/forward.
  useEffect(() => {
    const handler = () => setFilters(readFiltersFromUrl())
    window.addEventListener('popstate', handler)
    return () => window.removeEventListener('popstate', handler)
  }, [readFiltersFromUrl])

  // Build the API filter payload. Search filtering applies only to zone B
  // per E19 — it is sent to the equipment-tab endpoint, which already
  // ignores it for `derived_properties`.
  const apiFilters: EquipmentTabFilters = useMemo(() => {
    const out: EquipmentTabFilters = {}
    if (filters.q) out.q = filters.q
    if (filters.conditions.length > 0)
      out.conditions = filters.conditions.join(',')
    if (filters.trades.length > 0) out.trades = filters.trades.join(',')
    return out
  }, [filters])

  // For zone A we always show the unfiltered population. So we run two
  // queries: one without filters (zone A source of truth) and one with
  // filters (zone B). When no filter is active, they coincide.
  const noFilters: EquipmentTabFilters = useMemo(() => ({}), [])
  const isFilterActive =
    filters.q !== '' || filters.conditions.length > 0 || filters.trades.length > 0

  const baseQuery = useEquipmentTab(org, assetUid, noFilters)
  const filteredQuery = useEquipmentTab(
    org,
    isFilterActive ? assetUid : undefined,
    apiFilters,
  )

  // Same query key as the one useEquipmentTab already runs, so TanStack
  // dedupes it: we only need the raw rows to work out which ones arrived
  // through an aggregating relation rather than the physical tree.
  const { equipment: rawEquipment } = useAssetEquipment(org, assetUid)

  // What each equipment serves, read by its row (spec 2026-10-25-24).
  const { bySource: servesBySource } = useSubtreeServes(org, assetUid)

  const baseData = baseQuery.data
  const filteredData = isFilterActive ? filteredQuery.data : baseData

  const isError = baseQuery.isError && !baseData
  const errorStatus = (baseQuery.error as { status?: number } | undefined)?.status

  // Open the dialog with an optional trade preselected (looked up via the
  // bucket trade_code → asset-type uid mapping).
  const handleAddEquipment = useCallback(
    (tradeCode?: string) => {
      // Metering's + adds meters in series, the way the tab's menu does.
      if (tradeCode === METERING_TRADE_CODE) {
        setMetersOpen(true)
        return
      }
      const tradeUid = tradeCode
        ? assetTypes.find((at) => at.code === tradeCode)?.uid
        : undefined
      setDialogTradeUid(tradeUid)
      setDialogOpen(true)
    },
    [assetTypes],
  )

  // A reconciliation issue's "Complete the inventory" CTA (spec
  // 2026-07-24-04-declared-vs-observed-property-reconciliation.md) deep-links
  // here with `?tab=equipment&addEquipmentType=<code>` — open the Add
  // Equipment dialog pre-filtered on that type once the asset-type catalogue
  // is loaded, then strip the param so a refresh/back doesn't reopen it.
  useEffect(() => {
    if (typeof window === 'undefined' || assetTypes.length === 0) return
    const sp = new URLSearchParams(window.location.search)
    const preType = sp.get('addEquipmentType')
    if (!preType) return
    if (assetTypes.some((at) => at.code === preType)) {
      handleAddEquipment(preType)
    }
    const url = new URL(window.location.href)
    url.searchParams.delete('addEquipmentType')
    window.history.replaceState({}, '', url.toString())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetTypes])

  return (
    <ServesContext.Provider value={servesBySource}>
    <EquipmentViaProvider focalAssetUid={assetUid} equipment={rawEquipment}>
    <MeterSupplyProvider org={org} assetUid={assetUid}>
    <div className="space-y-4" data-testid="equipment-tab">
      {isError && (
        <div
          className="rounded-md border border-warning/30 bg-warning/5 p-3 text-sm text-warning"
          data-testid="equipment-tab-error"
        >
          {errorStatus === 404
            ? t('equipment_tab.error_404')
            : t('equipment_tab.error_generic')}
        </div>
      )}

      {/* One primary action (spec 2026-10-25-20): add a piece of equipment,
          with meters-in-series behind the split chevron — they are equipment
          too, added several at a time. */}
      <div className="flex justify-end">
        <div className="inline-flex">
          <Button
            type="button"
            size="sm"
            className="rounded-r-none"
            onClick={() => handleAddEquipment(undefined)}
            data-testid="equipment-tab-add-equipment-btn"
          >
            <AppIcon path={mdiPlus} size="sm" className="md:mr-1" />
            <span className="hidden md:inline">{t('equipment_tab.zone_b.add_equipment')}</span>
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                size="sm"
                className="rounded-l-none border-l border-primary-foreground/30 px-2"
                aria-label={t('equipment_tab.zone_b.add_more')}
                title={t('equipment_tab.zone_b.add_more')}
                data-testid="equipment-tab-add-menu"
              >
                <AppIcon path={mdiChevronDown} size="sm" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => setTimeout(() => setMetersOpen(true), 0)} data-testid="equipment-tab-add-meters-btn">
                <AppIcon path={mdiGauge} size="sm" className="mr-2" />
                {t('meters.add_button')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <ZoneBEquipmentByTrade
        org={org}
        data={filteredData}
        loading={isFilterActive ? filteredQuery.isLoading : baseQuery.isLoading}
        filters={filters}
        onFiltersChange={setFilters}
        onAddEquipment={handleAddEquipment}
        allowedTradeCodes={allowedTradeCodes}
        onAddComponent={setComponentOf}
      />

      <AddEquipmentDialog
        org={org}
        open={!!componentOf}
        onOpenChange={(open) => {
          if (!open) setComponentOf(undefined)
        }}
        parentAssetUid={assetUid}
        assemblyUid={componentOf}
      />

      <AddEquipmentDialog
        org={org}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        parentAssetUid={assetUid}
        preselectedTradeUid={dialogTradeUid}
        parentAssetLabel={baseData?.asset?.name}
      />

      <AddMetersDialog
        org={org}
        open={metersOpen}
        onOpenChange={setMetersOpen}
        assetUid={assetUid}
        isLot={isLotTypeCode(assetTypeCode)}
      />
    </div>
    </MeterSupplyProvider>
    </EquipmentViaProvider>
    </ServesContext.Provider>
  )
}
